import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Grid,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Description as DocumentIcon,
  AssignmentTurnedIn as ValidatedIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Document } from '../../features/cross-document/documentSetService';
import { brand } from '../../theme';
import { stepperIcons } from '../../assets/icons/nav';
import NavIcon from '../../shared/components/NavIcon';

interface DocumentSetUploadProps {
  onDocumentsUploaded: (files: File[]) => void;
  maxFiles?: number;
  documents: Document[];
  onRemoveDocument?: (documentId: string) => void;
  showDocumentTypes?: boolean;
}

const DOCUMENT_TYPE_COLORS: Record<string, string> = {
  commercial_invoice: '#4CAF50',
  bill_of_lading: '#2196F3',
  packing_list: '#FF9800',
  certificate_of_origin: '#9C27B0',
  health_certificate: '#F44336',
  customs_declaration: '#00BCD4',
  insurance_policy: '#795548',
  letter_of_credit: '#607D8B',
  unknown: '#9E9E9E',
};

// Utility function to safely extract error message
const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    // Handle Pydantic validation error format
    if (error.msg) return error.msg;
    if (error.message) return error.message;
    // Handle field-specific errors
    if (error.loc && Array.isArray(error.loc)) {
      const field = error.loc[error.loc.length - 1];
      return `${field}: ${error.msg || 'Invalid value'}`;
    }
    // Fallback to JSON
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown error';
    }
  }
  return 'Unknown error';
};

export const DocumentSetUpload: React.FC<DocumentSetUploadProps> = ({
  onDocumentsUploaded,
  maxFiles = 50,
  documents,
  onRemoveDocument,
  showDocumentTypes = true,
}) => {
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<Array<string | any>>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const newErrors = rejectedFiles.map(
          (file) => `${file.file.name}: ${file.errors[0]?.message || 'Invalid file'}`
        );
        setErrors(newErrors);
        setTimeout(() => setErrors([]), 5000);
      }

      // Handle accepted files
      if (acceptedFiles.length > 0) {
        // Check max files limit
        if (documents.length + acceptedFiles.length > maxFiles) {
          setErrors([`Maximum ${maxFiles} files allowed`]);
          return;
        }

        // Pass the actual File objects to parent for upload
        onDocumentsUploaded(acceptedFiles);
      }
    },
    [documents.length, maxFiles, onDocumentsUploaded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
    },
    maxFiles,
    multiple: true,
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <LinearProgress />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  const getDocumentTypeColor = (type?: string) => {
    if (!type) return DOCUMENT_TYPE_COLORS.unknown;
    const key = type.toLowerCase().replace(/[\s-]/g, '_');
    return DOCUMENT_TYPE_COLORS[key] || DOCUMENT_TYPE_COLORS.unknown;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        elevation={0}
        {...getRootProps()}
        sx={{
          p: 4,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? brand.orange : brand.borderSubtle,
          backgroundColor: brand.dropZoneBg,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': { borderColor: brand.orange },
        }}
      >
        <input {...getInputProps()} />
        <Box textAlign="center">
          <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
          <Typography variant="body1" gutterBottom sx={{ color: brand.textPrimary, fontWeight: 500 }}>
            {isDragActive
              ? 'Drop documents here...'
              : 'Drag & drop trade documents here'}
          </Typography>
          <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
            or click to browse files
          </Typography>
          <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block', mt: 0.5 }}>
            Supports: PDF, JPG, JPEG, PNG, TIFF (Max {maxFiles} files)
          </Typography>
        </Box>
      </Paper>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors([])}>
          {errors.map((error, index) => (
            <div key={index}>{getErrorMessage(error)}</div>
          ))}
        </Alert>
      )}

      {/* Document List */}
      {documents.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Documents ({documents.length}/{maxFiles})
            </Typography>
            {documents.some((d) => d.document_type) && (
              <Chip
                icon={<ValidatedIcon />}
                label="Types Detected"
                color="success"
                size="small"
              />
            )}
          </Box>

          <List>
            {documents.map((doc) => (
              <ListItem
                key={doc.id}
                sx={{
                  borderLeft: showDocumentTypes && doc.document_type ? 4 : 0,
                  borderColor: getDocumentTypeColor(doc.document_type),
                  mb: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <Box display="flex" alignItems="center" mr={2}>
                  <DocumentIcon color="action" />
                </Box>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">{doc.file_name}</Typography>
                      {showDocumentTypes && doc.document_type && (
                        <Chip
                          label={doc.document_type.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: getDocumentTypeColor(doc.document_type),
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                      {doc.document_type_confidence && (
                        <Tooltip title="Detection Confidence">
                          <Chip
                            label={`${Math.round(doc.document_type_confidence * 100)}%`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(doc.file_size)}
                      </Typography>
                      {doc.status === 'processing' && uploadProgress[doc.id] && (
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress[doc.id]}
                          sx={{ mt: 1, height: 2 }}
                        />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getStatusIcon(doc.status)}
                    {onRemoveDocument && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => onRemoveDocument(doc.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Document Type Summary */}
          {showDocumentTypes && documents.some((d) => d.document_type) && (
            <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
              <Typography variant="subtitle2" gutterBottom>
                Document Types Detected:
              </Typography>
              <Grid container spacing={1}>
                {Array.from(
                  new Set(documents.map((d) => d.document_type).filter(Boolean))
                ).map((type) => (
                  <Grid item key={type}>
                    <Chip
                      label={type!.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        backgroundColor: getDocumentTypeColor(type),
                        color: 'white',
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {/* Add More Button */}
      {documents.length > 0 && documents.length < maxFiles && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            {...getRootProps()}
            onClick={(e) => {
              e.stopPropagation();
              const input = document.querySelector('input[type="file"]') as HTMLInputElement;
              input?.click();
            }}
          >
            Add More Documents
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DocumentSetUpload;